import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../lib/api";
import { useTenant } from "../../lib/tenant";
import type { components } from "../../lib/api-types";

type AgentRunApproval = NonNullable<components["schemas"]["AgentRun"]["approval"]> & {
  decided_at?: string | null;
  reject_reason?: string | null;
};
type AgentRunApprovalDecision = components["schemas"]["AgentRunApprovalDecision"];
type AgentApprovalQueueItem = components["schemas"]["AgentApprovalQueueItem"];
type AgentApprovalQueueReport = components["schemas"]["AgentApprovalQueueReport"];

type AgentRun = components["schemas"]["AgentRun"] & {
  kind?: string;
  approval_token?: string;
  evidence_uri?: string;
  created_at?: string;
  decided_at?: string;
  approver?: string;
  reject_reason?: string;
  approval?: AgentRunApproval;
  audit_log?: Array<{ ts: string; action?: string; [k: string]: unknown }>;
  proposed_changes?:
    | string
    | {
        format?: "diff" | "json" | "markdown" | "text";
        body: string;
        target?: string;
      };
  expected_impact?: {
    metric?: string;
    direction?: "up" | "down" | "flat";
    eur?: number;
    confidence?: number;
  };
};

type ApprovalFilter = "all" | "pending" | "decided" | "no-evidence";

const APPROVAL_QUEUE_STATE_LABEL: Record<AgentApprovalQueueItem["approval_state"], string> = {
  pending: "Pending",
  expired: "Abgelaufen",
  decided: "Entschieden",
  missing_deadline: "Ohne Deadline",
};

const APPROVAL_QUEUE_STATE_CLASS: Record<AgentApprovalQueueItem["approval_state"], string> = {
  pending: "warn",
  expired: "bad",
  decided: "muted",
  missing_deadline: "warn",
};

const STATUS_COLOR: Record<string, string> = {
  blocked: "warn",
  approved: "good",
  rejected: "muted",
  succeeded: "good",
  failed: "bad",
  halted: "muted",
  expired: "muted",
  running: "accent",
  queued: "muted",
};

const KIND_LABEL: Record<string, string> = {
  mahnlauf: "Mahnlauf",
  "pricing-review": "Pricing-Review-Termin",
  "supplier-escalation": "Lieferanten-Eskalation",
  "marketing-budget-shift": "Marketing-Budget-Umschichtung",
};

function runLabel(run: AgentRun): string {
  return KIND_LABEL[run.kind ?? ""] ?? run.kind ?? run.agent ?? "Agent Run";
}

function runStatus(run: AgentRun): string {
  return String(run.status);
}

function approvalPending(run: AgentRun): boolean {
  const status = runStatus(run);
  return Boolean(
    run.approval?.required &&
      !run.approval.granted &&
      status !== "succeeded" &&
      status !== "failed" &&
      status !== "rejected",
  );
}

function approvalDecided(run: AgentRun): boolean {
  return Boolean(run.approval?.required && (run.approval.granted || run.approval.decided_at || run.approval.reject_reason));
}

function runHasEvidence(run: AgentRun): boolean {
  return Boolean(run.evidence_id || run.evidence_bundle || run.evidence_uri || (run.evidence_refs ?? []).length > 0);
}

function runTime(run: AgentRun): string | null {
  return run.started_at ?? run.created_at ?? null;
}

function approvalState(run: AgentRun): string {
  if (!run.approval?.required) return "not required";
  if (run.approval.granted) return "approved";
  if (run.approval.reject_reason) return "rejected";
  return "pending";
}

function outputText(run: AgentRun, key: string): string | null {
  const value = run.outputs?.[key];
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function outputApprovalDecision(run: AgentRun): AgentRunApprovalDecision | null {
  const value = run.outputs?.last_approval_decision;
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const decision = value as Partial<AgentRunApprovalDecision>;
  if (
    (decision.status === "approved" || decision.status === "rejected") &&
    typeof decision.run_id === "string" &&
    typeof decision.evidence_id === "string" &&
    typeof decision.evidence_ref === "string"
  ) {
    return decision as AgentRunApprovalDecision;
  }
  return null;
}

function formatDate(value?: string | null): string {
  if (!value) return "offen";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString("de-DE");
}

function formatDeadlineSeconds(value?: number | null): string {
  if (value === null || value === undefined) return "keine Deadline";
  const minutes = Math.round(Math.abs(value) / 60);
  if (value < 0) return `${minutes} min überfällig`;
  if (minutes < 1) return "fällig jetzt";
  return `${minutes} min bis Deadline`;
}

/**
 * Approval Panel.
 *
 * Lists all action runs for the current tenant. Runs with `approval.required`
 * stay blocked until a human approves or rejects them through the same
 * endpoints the e-mail-link approval flow uses.
 *
 * SelfCLAW: this is the cockpit-side mirror of the e-mail approval flow.
 * Both go through /v1/agent-runs/{run_id}/approve|reject with token + tenant
 * verification on the gateway.
 */
export function ApprovalsPanel() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<ApprovalFilter>("pending");
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [lastDecision, setLastDecision] = useState<AgentRunApprovalDecision | null>(null);

  const runs = useQuery({
    queryKey: ["agent-runs", effectiveTenantId],
    queryFn: () => api.get<AgentRun[]>("/agent-runs"),
    refetchInterval: 15_000,
  });

  const approvalQueue = useQuery({
    queryKey: ["agent-approval-queue", effectiveTenantId],
    queryFn: () => api.get<AgentApprovalQueueReport>("/agent-runs/approval-queue"),
    refetchInterval: 15_000,
  });

  const approve = useMutation({
    mutationFn: async (vars: { run_id: string; token: string }) =>
      api.post<AgentRunApprovalDecision>(
        `/agent-runs/${vars.run_id}/approve?token=${encodeURIComponent(vars.token)}&actor=operator-ui`,
        {},
      ),
    onSuccess: (decision) => {
      setLastDecision(decision);
      qc.invalidateQueries({ queryKey: ["agent-runs", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-approval-queue", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-records", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-integrity-report", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["evidence-registry", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-run-evidence-verification", effectiveTenantId, decision.run_id] });
    },
  });

  const reject = useMutation({
    mutationFn: async (vars: { run_id: string; token: string; reason?: string }) => {
      const url =
        `/agent-runs/${vars.run_id}/reject?token=${encodeURIComponent(vars.token)}&actor=operator-ui` +
        (vars.reason ? `&reason=${encodeURIComponent(vars.reason)}` : "");
      return api.post<AgentRunApprovalDecision>(url, {});
    },
    onSuccess: (decision) => {
      setLastDecision(decision);
      qc.invalidateQueries({ queryKey: ["agent-runs", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-approval-queue", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-records", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-integrity-report", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["evidence-registry", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-run-evidence-verification", effectiveTenantId, decision.run_id] });
    },
  });

  const filtered = useMemo(() => {
    const list = runs.data ?? [];
    if (filter === "pending") return list.filter(approvalPending);
    if (filter === "decided") return list.filter((run) => approvalDecided(run) || !approvalPending(run));
    if (filter === "no-evidence") return list.filter((run) => !runHasEvidence(run));
    return list;
  }, [runs.data, filter]);

  const counts = useMemo(() => {
    const list = runs.data ?? [];
    const pending = list.filter(approvalPending);
    return {
      pending: pending.length,
      decided: list.filter((run) => approvalDecided(run) || !approvalPending(run)).length,
      missingEvidence: list.filter((run) => !runHasEvidence(run)).length,
      tokenReady: pending.filter((run) => Boolean(run.approval_token)).length,
      required: list.filter((run) => run.approval?.required).length,
      evidenceReady: list.filter(runHasEvidence).length,
      total: list.length,
    };
  }, [runs.data]);

  return (
    <div className="approvals">
      <ActionApprovalGate counts={counts} tenantId={effectiveTenantId} loading={runs.isLoading} error={runs.error} />
      <ApprovalQueuePanel
        report={approvalQueue.data}
        loading={approvalQueue.isLoading}
        fetching={approvalQueue.isFetching}
        error={approvalQueue.error}
      />
      {lastDecision && <ApprovalDecisionEvidence decision={lastDecision} />}

      <div className="approvals-header">
        <div className="filters">
          <button
            className={`btn ghost ${filter === "pending" ? "active" : ""}`}
            onClick={() => setFilter("pending")}
          >
            Pending <span className="pill warn">{counts.pending}</span>
          </button>
          <button
            className={`btn ghost ${filter === "decided" ? "active" : ""}`}
            onClick={() => setFilter("decided")}
          >
            Entschieden <span className="pill muted">{counts.decided}</span>
          </button>
          <button
            className={`btn ghost ${filter === "no-evidence" ? "active" : ""}`}
            onClick={() => setFilter("no-evidence")}
          >
            Ohne Evidence <span className="pill muted">{counts.missingEvidence}</span>
          </button>
          <button
            className={`btn ghost ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            Alle <span className="pill muted">{counts.total}</span>
          </button>
        </div>
        <span className="grow" />
        <span className="muted small">
          {runs.isFetching ? "Aktualisiere..." : "Auto-refresh alle 15s"}
        </span>
      </div>

      {runs.isLoading && <p className="muted">Lade...</p>}
      {runs.error && <p className="error">Fehler beim Laden der Agent-Runs.</p>}
      {!runs.isLoading && filtered.length === 0 && (
        <p className="muted">
          {filter === "pending"
            ? "Keine offenen Approvals. Der Action-Orchestrator bleibt default-deny, bis neue Maßnahmen eine Freigabe brauchen."
            : "Keine Einträge für diesen Filter."}
        </p>
      )}

      <ul className="run-list">
        {filtered.map((run) => {
          const isPending = approvalPending(run);
          const isExpanded = expandedRun === run.run_id;
          const statusClass = STATUS_COLOR[runStatus(run)] ?? "muted";
          const created = runTime(run);
          const evidenceRef = run.evidence_uri ?? run.evidence_bundle ?? run.evidence_id ?? outputText(run, "evidence_id");
          const decision = outputApprovalDecision(run);
          return (
            <li key={run.run_id} className={`run-item ${isExpanded ? "expanded" : ""} ${isPending ? "approval-pending" : ""}`}>
              <div className="run-row" onClick={() => setExpandedRun(isExpanded ? null : run.run_id)}>
                <div className="run-kind">
                  <strong>{runLabel(run)}</strong>
                  <span className="muted small"> · {run.run_id}</span>
                </div>
                <span className={`pill ${isPending ? "warn" : "muted"}`}>{approvalState(run)}</span>
                <span className={`pill ${statusClass}`}>{runStatus(run)}</span>
                <span className="grow" />
                {created && <span className="muted small">{formatDate(created)}</span>}
              </div>

              {isExpanded && (
                <div className="run-detail">
                  <div className="approval-run-grid">
                    <div><span>Tenant</span><strong>{run.tenant_id}</strong></div>
                    <div><span>Approval</span><strong>{approvalState(run)}</strong></div>
                    <div><span>Approver</span><strong>{run.approval?.approvers?.join(", ") || "nicht erforderlich"}</strong></div>
                    <div><span>Evidence</span><strong>{runHasEvidence(run) ? "vorhanden" : "fehlt"}</strong></div>
                    <div><span>Model Route</span><strong>{run.model_route ?? outputText(run, "model_route") ?? run.llm_calls?.[0]?.model ?? "local/runtime"}</strong></div>
                    <div><span>Audit Namespace</span><strong>{run.audit_namespace ?? outputText(run, "audit_namespace") ?? run.tenant_id}</strong></div>
                  </div>

                  {run.expected_impact && (
                    <div className="expected-impact">
                      <span className="kv-label">Erwarteter Impact</span>
                      <div>
                        {run.expected_impact.metric && <strong>{run.expected_impact.metric}</strong>}
                        {run.expected_impact.eur !== undefined && (
                          <span
                            className={`pill ${
                              run.expected_impact.direction === "up"
                                ? "good"
                                : run.expected_impact.direction === "down"
                                ? "bad"
                                : "muted"
                            }`}
                          >
                            {new Intl.NumberFormat("de-DE", {
                              style: "currency",
                              currency: "EUR",
                              maximumFractionDigits: 0,
                              signDisplay: "always",
                            }).format(run.expected_impact.eur)}
                          </span>
                        )}
                        {run.expected_impact.confidence !== undefined && (
                          <span className="pill muted">
                            Konfidenz {Math.round(run.expected_impact.confidence * 100)}%
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {run.proposed_changes && <ProposedChanges value={run.proposed_changes} />}

                  {evidenceRef && (
                    <div>
                      <span className="muted small">Evidence:</span>{" "}
                      <code>{evidenceRef}</code>
                    </div>
                  )}

                  {(run.decided_at || run.approval?.decided_at) && (
                    <div>
                      <span className="muted small">Entschieden:</span>{" "}
                      {formatDate(run.decided_at ?? run.approval?.decided_at)} · von <code>{run.approver ?? run.approval?.decided_by ?? "approval-flow"}</code>
                      {(run.reject_reason || run.approval?.reject_reason) && <> · Grund: {run.reject_reason ?? run.approval?.reject_reason}</>}
                    </div>
                  )}

                  {decision && <ApprovalDecisionEvidence decision={decision} compact />}

                  {run.audit_log && run.audit_log.length > 0 && (
                    <details>
                      <summary>Audit-Log ({run.audit_log.length} Einträge)</summary>
                      <ul className="audit-log">
                        {run.audit_log.slice(-10).map((entry, index) => (
                          <li key={index}>
                            <span className="muted small">{entry.ts}</span> {String(entry.action ?? JSON.stringify(entry).slice(0, 120))}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {isPending && run.approval_token && (
                    <div className="run-actions">
                      <button
                        className="btn good"
                        disabled={approve.isPending}
                        onClick={() => {
                          if (window.confirm(`Genehmigen: ${runLabel(run)}?`)) {
                            approve.mutate({ run_id: run.run_id, token: run.approval_token! });
                          }
                        }}
                      >
                        Genehmigen
                      </button>
                      <button
                        className="btn bad"
                        disabled={reject.isPending}
                        onClick={() => {
                          const reason = window.prompt("Grund der Ablehnung (optional):") ?? undefined;
                          if (window.confirm(`Ablehnen: ${run.run_id}?`)) {
                            reject.mutate({ run_id: run.run_id, token: run.approval_token!, reason });
                          }
                        }}
                      >
                        Ablehnen
                      </button>
                    </div>
                  )}

                  {isPending && !run.approval_token && (
                    <div className="approval-token-state">
                      Approval ist offen, aber der Cockpit-Token wird nicht an den Browser ausgeliefert. Entscheidung erfolgt über den gesicherten Mail-, Teams- oder Operator-Link.
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {(approve.error || reject.error) && (
        <div className="error">
          Fehler bei Aktion: {(approve.error as { message?: string } | null)?.message ?? (reject.error as { message?: string } | null)?.message}
        </div>
      )}
    </div>
  );
}

function ApprovalQueuePanel({
  report,
  loading,
  fetching,
  error,
}: {
  report?: AgentApprovalQueueReport;
  loading: boolean;
  fetching: boolean;
  error: unknown;
}) {
  const items = report?.items ?? [];
  const blocked = (report?.default_deny_ready_count ?? 0) > 0 || (report?.expired_count ?? 0) > 0 || (report?.missing_deadline_count ?? 0) > 0;
  return (
    <section className={`approval-queue-panel ${blocked ? "blocked" : "ready"}`}>
      <div className="approval-queue-head">
        <div>
          <p className="hero-eyebrow">Approval Queue</p>
          <h3>Timeouts, fehlende Deadlines und entschiedene HITL-Läufe.</h3>
          <p className="muted small">
            Token-sicheres Lesemodell aus <code>/v1/agent-runs/approval-queue</code>; Default-deny bleibt sichtbar, bevor Aktionen nach aussen gehen.
          </p>
        </div>
        <span className={`status-pill ${loading ? "warning" : error ? "danger" : blocked ? "warning" : "good"}`}>
          {loading ? "lade" : error ? "api error" : blocked ? "review" : fetching ? "sync" : "ready"}
        </span>
      </div>

      <div className="approval-queue-grid">
        <div><span>Approval Required</span><strong>{report?.approval_required_count ?? "..."}</strong></div>
        <div><span>Pending</span><strong>{report?.pending_count ?? "..."}</strong></div>
        <div><span>Expired</span><strong>{report?.expired_count ?? "..."}</strong></div>
        <div><span>Default-deny ready</span><strong>{report?.default_deny_ready_count ?? "..."}</strong></div>
        <div><span>Missing deadline</span><strong>{report?.missing_deadline_count ?? "..."}</strong></div>
        <div><span>Decided</span><strong>{report?.decided_count ?? "..."}</strong></div>
      </div>

      {items.length > 0 ? (
        <ul className="approval-queue-list">
          {items.slice(0, 5).map((item) => (
            <li key={item.run_id} className={`state-${item.approval_state}`}>
              <div>
                <strong>{item.agent}</strong>
                <span className="muted small">{item.run_id}</span>
              </div>
              <span className={`pill ${APPROVAL_QUEUE_STATE_CLASS[item.approval_state]}`}>
                {APPROVAL_QUEUE_STATE_LABEL[item.approval_state]}
              </span>
              <span className="muted small">{formatDeadlineSeconds(item.seconds_until_deadline)}</span>
              <span className={`pill ${item.default_deny_ready ? "warn" : "muted"}`}>
                {item.default_deny_ready ? "default-deny" : item.decision ?? item.run_status}
              </span>
              <span className={`pill ${item.evidence_ready ? "good" : "muted"}`}>
                {item.evidence_ready ? "Evidence" : "No evidence"}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">
          Keine approval-required Runs in der Queue. Neue externe Aktionen erscheinen hier mit Tenant-Scope, Deadline und Evidence-Status.
        </p>
      )}

      {Boolean(error) && <p className="error">Approval Queue konnte nicht geladen werden.</p>}
    </section>
  );
}

function ApprovalDecisionEvidence({
  decision,
  compact = false,
}: {
  decision: AgentRunApprovalDecision;
  compact?: boolean;
}) {
  return (
    <section className={`approval-decision-evidence ${decision.status} ${compact ? "compact" : ""}`}>
      <div>
        <span className={`pill ${decision.status === "approved" ? "good" : "warn"}`}>
          {decision.status === "approved" ? "approved" : "rejected"}
        </span>
        <strong>Decision Evidence</strong>
        <p className="muted small">
          {formatDate(decision.decided_at)} · {decision.decided_by} · Run {decision.run_id}
        </p>
      </div>
      <div className="approval-decision-grid">
        <div><span>Run Status</span><strong>{decision.run_status}</strong></div>
        <div><span>Evidence ID</span><strong>{decision.evidence_id}</strong></div>
        <div><span>Evidence Ref</span><strong>{decision.evidence_ref}</strong></div>
        <div><span>Refs</span><strong>{decision.evidence_refs?.length ?? 0}</strong></div>
      </div>
      {decision.reason && <p className="muted small">Grund: {decision.reason}</p>}
    </section>
  );
}

function ActionApprovalGate({
  counts,
  tenantId,
  loading,
  error,
}: {
  counts: {
    pending: number;
    decided: number;
    missingEvidence: number;
    tokenReady: number;
    required: number;
    evidenceReady: number;
    total: number;
  };
  tenantId: string | null;
  loading: boolean;
  error: unknown;
}) {
  const blocked = counts.pending > 0 || counts.missingEvidence > 0;
  return (
    <section className={`action-approval-gate ${blocked ? "blocked" : "ready"}`}>
      <div>
        <p className="hero-eyebrow">Action-Orchestrator HITL Gate</p>
        <h2>Keine externe Aktion ohne Approval, Evidence und Tenant-Scope.</h2>
        <p className="muted small">
          Pricing, Lieferanten-Eskalation, Budget-Shift und Prozessmaßnahmen bleiben default-deny, bis ein berechtigter Mensch die Aktion mit Evidence freigibt.
        </p>
      </div>
      <span className={`status-pill ${loading ? "warning" : error ? "danger" : blocked ? "warning" : "good"}`}>
        {loading ? "lade" : error ? "api error" : blocked ? "review" : "ready"}
      </span>
      <div className="action-approval-grid">
        <div><span>Tenant</span><strong>{tenantId ?? "unknown"}</strong></div>
        <div><span>Runs</span><strong>{counts.total}</strong></div>
        <div><span>Approval Required</span><strong>{counts.required}</strong></div>
        <div><span>Pending</span><strong>{counts.pending}</strong></div>
        <div><span>Evidence Ready</span><strong>{counts.evidenceReady}</strong></div>
        <div><span>Missing Evidence</span><strong>{counts.missingEvidence}</strong></div>
        <div><span>Cockpit Tokens</span><strong>{counts.tokenReady}</strong></div>
        <div><span>Decision Evidence</span><strong>typed</strong></div>
        <div><span>Default</span><strong>deny</strong></div>
      </div>
    </section>
  );
}

function ProposedChanges({
  value,
}: {
  value: NonNullable<AgentRun["proposed_changes"]>;
}) {
  const body = typeof value === "string" ? value : value.body;
  const format =
    typeof value === "string" ? detectFormat(value) : (value.format ?? detectFormat(value.body));
  const target = typeof value === "string" ? undefined : value.target;
  return (
    <div className="proposed-changes">
      <div className="proposed-head">
        <span className="kv-label">Vorgeschlagene Änderung</span>
        {target && <code className="small muted">{target}</code>}
        <span className="grow" />
        <span className="pill muted small">{format}</span>
      </div>
      <pre className={`proposed-body fmt-${format}`}>
        {format === "diff" ? renderDiff(body) : body}
      </pre>
    </div>
  );
}

function detectFormat(text: string): "diff" | "json" | "markdown" | "text" {
  const trimmed = text.trimStart();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return "json";
  if (/^[+-]/m.test(trimmed) && /^@@/m.test(trimmed)) return "diff";
  if (/^[+-]/m.test(trimmed)) return "diff";
  if (/^#{1,6}\s/m.test(trimmed)) return "markdown";
  return "text";
}

function renderDiff(body: string) {
  return body.split("\n").map((line, index) => {
    const cls = line.startsWith("+")
      ? "diff-add"
      : line.startsWith("-")
      ? "diff-del"
      : line.startsWith("@@")
      ? "diff-hunk"
      : "diff-ctx";
    return (
      <span key={index} className={cls}>
        {line}
        {"\n"}
      </span>
    );
  });
}
